(() => {
  const normalizeOptionName = (name) => {
    const normalized = String(name || '').trim().toLocaleLowerCase().replace(/\s+/g, ' ');
    return normalized === 'colour' ? 'color' : normalized;
  };

  const normalizeOptionValue = (value) => String(value || '').trim().toLocaleLowerCase();

  const productOptionDefinitions = (productData) =>
    Array.from(productData?.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : option?.name,
      normalizedName: normalizeOptionName(typeof option === 'string' ? option : option?.name),
      position: Number.parseInt(typeof option === 'string' ? index + 1 : option?.position || index + 1, 10),
    }));

  const variantOptions = (variant, definitions) =>
    Array.isArray(variant?.options)
      ? variant.options
      : definitions.map((definition) => variant?.[`option${definition.position}`]);

  const exactVariantForSelections = ({ productData, selections }) => {
    const definitions = productOptionDefinitions(productData);
    if (definitions.some(({ normalizedName }) => !selections.has(normalizedName))) return null;
    return (
      Array.from(productData?.variants || []).find((variant) => {
        const options = variantOptions(variant, definitions);
        return definitions.every(
          ({ normalizedName }, index) =>
            normalizeOptionValue(options[index]) === normalizeOptionValue(selections.get(normalizedName)),
        );
      }) || null
    );
  };

  const optionValueExists = ({ productData, selections, optionName, optionValue }) => {
    const definitions = productOptionDefinitions(productData);
    const normalizedName = normalizeOptionName(optionName);
    return Array.from(productData?.variants || []).some((variant) => {
      const options = variantOptions(variant, definitions);
      return definitions.every((definition, index) => {
        const expected =
          definition.normalizedName === normalizedName
            ? optionValue
            : selections.get(definition.normalizedName);
        return !expected || normalizeOptionValue(options[index]) === normalizeOptionValue(expected);
      });
    });
  };

  const parseProductData = (root) => {
    try {
      const productData = JSON.parse(root?.querySelector('[data-scoreggia-handoff-product]')?.textContent || 'null');
      return Array.isArray(productData?.variants) ? productData : null;
    } catch {
      return null;
    }
  };

  const handoffConfigurationFromUrl = ({ root, locationHref }) => {
    try {
      const url = new URL(locationHref);
      const productUrl = new URL(root?.dataset.url || '', url.origin);
      const mode = url.searchParams.get('sg_handoff');
      const mediaId = url.searchParams.get('sg_media');
      if (
        url.pathname !== productUrl.pathname ||
        (mode !== 'color' && mode !== 'media') ||
        !/^\d+$/.test(mediaId || '')
      ) {
        return null;
      }
      const selections = {};
      try {
        const serializedSelections = JSON.parse(url.searchParams.get('sg_options') || 'null');
        if (serializedSelections && typeof serializedSelections === 'object') {
          Object.entries(serializedSelections).forEach(([name, value]) => {
            if (typeof value === 'string' && value) selections[normalizeOptionName(name)] = value;
          });
        }
      } catch {}
      if (mode === 'color' && url.searchParams.get('sg_color')) {
        selections.color = url.searchParams.get('sg_color');
      }
      return { mediaId, mode, selections };
    } catch {
      return null;
    }
  };

  window.ScoreggiaProductHandoffCore = {
    exactVariantForSelections,
    handoffConfigurationFromUrl,
    normalizeOptionName,
    optionValueExists,
    productOptionDefinitions,
  };

  const managers = new WeakMap();

  class ScoreggiaProductHandoffManager {
    constructor(root) {
      this.root = root;
      this.productData = parseProductData(root);
      this.definitions = productOptionDefinitions(this.productData);
      this.explicitSelections = new Map();
      this.expectedVariantId = null;
      this.abortController = new AbortController();
      this.root.addEventListener('change', (event) => this.handleOptionChange(event), {
        capture: true,
        signal: this.abortController.signal,
      });
      this.root.addEventListener('product-info:variant-rendered', (event) => this.handleVariantRendered(event), {
        signal: this.abortController.signal,
      });
      this.root.addEventListener('product-info:variant-render-error', () => {
        if (this.root.dataset.scoreggiaHandoffState === 'resolving') this.setPartialState();
      }, {
        signal: this.abortController.signal,
      });
    }

    get picker() {
      return this.root.querySelector('variant-selects');
    }

    get groups() {
      if (!this.picker) return [];
      return Array.from(this.picker.querySelectorAll('fieldset, .product-form__input--dropdown'))
        .map((element) => {
          const select = element.matches('.product-form__input--dropdown')
            ? element.querySelector('select')
            : null;
          const controls = select ? Array.from(select.options) : Array.from(element.querySelectorAll('input'));
          const firstControl = controls[0];
          const rawName =
            firstControl?.dataset.optionName ||
            select?.name?.replace(/^options\[|\]$/g, '') ||
            element.dataset.scoreggiaOption ||
            '';
          return {
            controls,
            element,
            name: normalizeOptionName(rawName),
            select,
          };
        })
        .filter(({ name, controls }) => name && controls.length);
    }

    apply(configuration) {
      const singleVariantWithoutPicker =
        this.groups.length === 0 && Array.from(this.productData?.variants || []).length === 1;
      if (
        !this.productData ||
        (!singleVariantWithoutPicker && this.definitions.length !== this.groups.length)
      ) {
        return false;
      }
      const mediaId = String(configuration?.mediaId || '');
      const mediaTarget = mediaId
        ? this.root.querySelector(`[data-media-id="${this.root.dataset.section}-${mediaId}"]`)
        : null;
      if (mediaId && !mediaTarget) return false;

      this.clearSelections();
      Object.entries(configuration?.selections || {}).forEach(([name, value]) => {
        this.selectOption(name, value);
      });
      if (configuration?.mode === 'color' && !this.explicitSelections.has('color')) return false;

      this.updateCompatibility();
      const exactVariant = singleVariantWithoutPicker
        ? this.productData.variants[0]
        : exactVariantForSelections({
            productData: this.productData,
            selections: this.explicitSelections,
          });
      if (exactVariant) this.complete(exactVariant, { initial: true });
      else this.setPartialState();

      if (mediaId) {
        const gallery = this.root.querySelector('media-gallery');
        if (!gallery?.setInitialHandoffMedia?.(`${this.root.dataset.section}-${mediaId}`)) return false;
      }
      return true;
    }

    clearSelections() {
      this.explicitSelections.clear();
      this.groups.forEach(({ controls, element, select }) => {
        element.setAttribute('data-scoreggia-awaiting-selection', '');
        controls.forEach((control) => {
          control.removeAttribute('data-scoreggia-explicit');
          if (select) {
            control.selected = false;
            control.removeAttribute('selected');
          } else {
            control.checked = false;
            control.removeAttribute('checked');
          }
        });
        if (select) select.selectedIndex = -1;
        const selectedValue = element.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.textContent = '';
      });
    }

    selectOption(name, value) {
      const normalizedName = normalizeOptionName(name);
      const group = this.groups.find((candidate) => candidate.name === normalizedName);
      if (!group) return false;
      const control = group.controls.find(
        (candidate) => normalizeOptionValue(candidate.value) === normalizeOptionValue(value),
      );
      if (!control) return false;

      if (group.select) {
        group.select.value = control.value;
        control.selected = true;
        control.setAttribute('selected', 'selected');
      } else {
        control.checked = true;
        control.setAttribute('checked', '');
      }
      control.dataset.scoreggiaExplicit = 'true';
      group.element.removeAttribute('data-scoreggia-awaiting-selection');
      const selectedValue = group.element.querySelector('[data-selected-value]');
      if (selectedValue) selectedValue.textContent = control.value;
      this.explicitSelections.set(normalizedName, control.value);
      return true;
    }

    updateCompatibility() {
      this.groups.forEach((group) => {
        group.controls.forEach((control) => {
          const possible = optionValueExists({
            productData: this.productData,
            selections: this.explicitSelections,
            optionName: group.name,
            optionValue: control.value,
          });
          const selected = group.select ? control.selected : control.checked;
          control.toggleAttribute('data-scoreggia-impossible', !possible && !selected);
          control.disabled = !possible && !selected;
        });
      });
    }

    handleOptionChange(event) {
      const picker = event.target.closest('variant-selects');
      if (!picker || picker !== this.picker || !this.root.contains(event.target)) return;
      const target = event.target.matches('select') ? event.target.selectedOptions?.[0] : event.target;
      const rawName =
        target?.dataset.optionName ||
        event.target.name?.replace(/^options\[|\]$/g, '') ||
        event.target.closest('[data-scoreggia-option]')?.dataset.scoreggiaOption;
      const name = normalizeOptionName(rawName);
      if (!name || !target?.value) return;

      const group = this.groups.find((candidate) => candidate.name === name);
      group?.controls.forEach((control) => control.removeAttribute('data-scoreggia-explicit'));
      target.dataset.scoreggiaExplicit = 'true';
      group?.element.removeAttribute('data-scoreggia-awaiting-selection');
      const selectedValue = group?.element.querySelector('[data-selected-value]');
      if (selectedValue) selectedValue.textContent = target.value;
      this.explicitSelections.set(name, target.value);
      this.updateCompatibility();

      const variant = exactVariantForSelections({
        productData: this.productData,
        selections: this.explicitSelections,
      });
      if (!variant) {
        event.stopImmediatePropagation();
        this.setPartialState();
        return;
      }

      this.expectedVariantId = String(variant.id);
      this.setResolvingState();
    }

    handleVariantRendered(event) {
      if (this.root.dataset.scoreggiaHandoffState !== 'resolving') return;
      const variant = event.detail?.variant;
      if (!variant || String(variant.id) !== this.expectedVariantId) {
        this.setPartialState();
        return;
      }
      this.groups.forEach((group) => {
        const control = group.select
          ? group.select.selectedOptions?.[0]
          : group.controls.find((candidate) => candidate.checked);
        if (!control) return;
        control.dataset.scoreggiaExplicit = 'true';
        this.explicitSelections.set(group.name, control.value);
      });
      this.complete(variant);
    }

    setPartialState() {
      this.expectedVariantId = null;
      this.root.dataset.scoreggiaHandoffState = 'partial';
      this.neutralizeCommerce();
    }

    setResolvingState() {
      this.root.dataset.scoreggiaHandoffState = 'resolving';
      this.neutralizeCommerce();
    }

    neutralizeCommerce() {
      this.root.querySelectorAll('.product__buy-buttons').forEach((element) => {
        element.dataset.scoreggiaHandoffHidden = 'true';
        element.hidden = true;
      });
      this.root
        .querySelectorAll(
          '.product__inventory, .product__sku, pickup-availability, form.installment, shopify-payment-terms, [id^="Quantity-Rules-"], [id^="Volume-"]',
        )
        .forEach((element) => {
          element.dataset.scoreggiaHandoffNeutral = 'true';
          element.hidden = true;
        });
      this.root
        .querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"], form.installment input[name="id"]')
        .forEach((input) => {
          input.value = '';
          input.disabled = true;
        });

      const priceCandidate = document.getElementById(`price-${this.root.dataset.section}`);
      const price = this.root.contains(priceCandidate) ? priceCandidate : null;
      const template = this.root.querySelector('[data-scoreggia-product-price]');
      if (price && template && !price.hasAttribute('data-scoreggia-product-level-price')) {
        price.innerHTML = template.innerHTML;
        price.dataset.scoreggiaProductLevelPrice = 'true';
      }

      const quantity = this.root.querySelector('.quantity__input');
      if (quantity) {
        quantity.min = '1';
        quantity.step = '1';
        quantity.removeAttribute('max');
        quantity.value = '1';
      }
    }

    complete(variant, { initial = false } = {}) {
      this.expectedVariantId = null;
      this.root.dataset.scoreggiaHandoffState = 'complete';
      this.root.querySelectorAll('[data-scoreggia-handoff-hidden]').forEach((element) => {
        element.hidden = false;
        element.removeAttribute('data-scoreggia-handoff-hidden');
      });
      this.root.querySelectorAll('[data-scoreggia-handoff-neutral]').forEach((element) => {
        element.hidden = false;
        element.removeAttribute('data-scoreggia-handoff-neutral');
      });
      this.root
        .querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"], form.installment input[name="id"]')
        .forEach((input) => {
          input.value = String(variant.id);
          input.disabled = false;
        });

      const productForm = this.root.querySelector('product-form');
      productForm?.toggleSubmitButton?.(variant.available !== true, variant.available ? undefined : window.variantStrings.soldOut);
      if (!initial) this.root.querySelector('[data-scoreggia-product-level-price]')?.removeAttribute('data-scoreggia-product-level-price');
      this.cleanHandoffUrl(variant.id);
      this.root.dispatchEvent(
        new CustomEvent('scoreggia-handoff:complete', {
          bubbles: true,
          detail: { variant },
        }),
      );
    }

    cleanHandoffUrl(variantId) {
      const url = new URL(window.location.href);
      ['sg_handoff', 'sg_media', 'sg_color', 'sg_options'].forEach((key) => url.searchParams.delete(key));
      if (variantId) url.searchParams.set('variant', String(variantId));
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
    }

    snapshot() {
      if (
        this.root.dataset.scoreggiaHandoffState !== 'partial' &&
        this.root.dataset.scoreggiaHandoffState !== 'resolving'
      ) return null;
      return { mode: 'selections', selections: Object.fromEntries(this.explicitSelections) };
    }
  }

  const reveal = () => {
    window.clearTimeout(window.scoreggiaHandoffRevealTimer);
    document.documentElement.classList.remove('scoreggia-handoff-pending');
    delete document.documentElement.dataset.scoreggiaHandoff;
  };

  const apply = (root, configuration) => {
    if (!root?.matches('product-info[data-scoreggia-family="true"]')) return false;
    managers.get(root)?.abortController.abort();
    const manager = new ScoreggiaProductHandoffManager(root);
    managers.set(root, manager);
    return manager.apply(configuration);
  };

  window.ScoreggiaProductHandoff = {
    apply,
    capture(root) {
      return managers.get(root)?.snapshot() || null;
    },
  };

  const initialize = async () => {
    if (document.documentElement.dataset.scoreggiaHandoff !== 'true') return;
    const root = document.querySelector('product-info[data-scoreggia-family="true"]');
    const configuration = handoffConfigurationFromUrl({ root, locationHref: window.location.href });
    if (!root || !configuration) {
      reveal();
      return;
    }
    const definitions = ['product-info'];
    if (root.querySelector('media-gallery')) definitions.push('media-gallery');
    await Promise.all(definitions.map((name) => customElements.whenDefined(name)));
    apply(root, configuration);
    reveal();
  };

  initialize().catch(reveal);
})();
